import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCategoryList } from "../../apis/category";
import toast from 'react-hot-toast';
import useCreate from '../useCreate';

function useCategoryManagement(productListData) {
  // States for category modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [mappedCategories, setMappedCategories] = useState([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  
  // Add search and pagination state
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    limit: 20,
    page_number: 0
  });
  
  const queryClient = useQueryClient();

  // Fetch all available categories when the modal is open
  const { data: categoriesData, isLoading: categoriesLoading, refetch: refetchCategories } = useQuery(
    ["categoryList", searchQuery, pagination], 
    () => getCategoryList({ 
      queryKey: [null, { 
        paramsObject: { 
          limit: pagination.limit, 
          pageNo: pagination.page_number,
          searchQuery: searchQuery 
        } 
      }] 
    }),
    {
      enabled: isCategoryModalOpen,
      refetchOnWindowFocus: false,
    }
  );

  // Update category lists when modal opens or data changes
  useEffect(() => {
    if (!isCategoryModalOpen || !selectedProduct) return;
    
    // Update mapped categories from the product data
    setMappedCategories(selectedProduct.product_categories || []);
    
    // If categories are loaded, update the all categories list
    if (categoriesData?.data?.data) {
      setAllCategories(categoriesData.data.data);
    }
  }, [isCategoryModalOpen, selectedProduct, categoriesData?.data?.data]);

  // Handle search for categories
  const handleSearchCategories = (query) => {
    setSearchQuery(query);
    // Reset pagination when searching
    setPagination(prev => ({
      ...prev,
      page_number: 0
    }));
  };

  // Function to refresh modal data from the product list
  const refreshModalWithLatestData = () => {
    if (selectedProduct && isCategoryModalOpen && productListData?.data?.data) {
      // Find the current product in the refreshed product list
      const updatedProduct = productListData.data.data.find(
        product => product.product_id === selectedProduct.product_id
      );
      
      if (updatedProduct) {
        // Update the modal state with fresh data
        setSelectedProduct(updatedProduct);
        setMappedCategories(updatedProduct.product_categories || []);
      }
    }
  };

  // Watch for changes in productListData to refresh modal when API data changes
  useEffect(() => {
    refreshModalWithLatestData();
  }, [productListData?.data]);

  // Create category-product mapping
  const { setDataToServer: addCategoryToProduct, isLoading: isAddingCategory } = useCreate({
    url: "create-product-category-map",
    refreshUrl: "get-product-list",
    onSuccess: () => {
      toast.success("Category added to product", {
        position: "top-right",
        duration: 3000,
      });
      // No need to manually update state - refreshModalWithLatestData will handle it
    },
    onError: (err) => {
      toast.error(`Error adding category: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
    }
  });

  // Delete category-product mapping
  const { setDataToServer: removeCategoryFromProduct, isLoading: isRemovingCategory } = useCreate({
    url: "delete-product-category-map",
    refreshUrl: "get-product-list",
    onSuccess: () => {
      toast.success("Category removed from product", {
        position: "top-right",
        duration: 3000,
      });
      // No need to manually update state - refreshModalWithLatestData will handle it
    },
    onError: (err) => {
      toast.error(`Error removing category: ${err?.message || 'Unknown error'}`, {
        position: "top-right",
        duration: 3000,
      });
    }
  });

  // Function to check if a category is already mapped to the product
  const isCategoryMapped = (categoryId) => {
    return mappedCategories.some(cat => cat.category_id === categoryId);
  };

  // Get mapped category by ID
  const getMappedCategory = (categoryId) => {
    return mappedCategories.find(cat => cat.category_id === categoryId);
  };

  // Handler to add category to product
  const handleAddCategory = (categoryId) => {
    if (!selectedProduct?.product_id) return;
    
    addCategoryToProduct.mutate({
      product_id: selectedProduct.product_id,
      category_id: categoryId
    });
  };

  // Handler to remove category from product
  const handleRemoveCategory = (mapId) => {
    if (!mapId) return;
    
    removeCategoryFromProduct.mutate({
      product_category_map_id: mapId
    });
  };

  // Reset search when closing the modal
  useEffect(() => {
    if (!isCategoryModalOpen) {
      setSearchQuery("");
    }
  }, [isCategoryModalOpen]);

  // Category modal handlers
  const handleOpenCategoryModal = (product) => {
    setSelectedProduct(product);
    setMappedCategories(product.product_categories || []);
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setSelectedProduct(null);
    setMappedCategories([]);
    setSearchQuery("");
  };

  return {
    // Category management states
    selectedProduct,
    allCategories,
    mappedCategories,
    isCategoryModalOpen,
    categoriesLoading,
    isCategoryMapped,
    getMappedCategory,
    searchQuery,
    
    // Category modal handlers
    handleOpenCategoryModal,
    handleCloseCategoryModal,
    handleSearchCategories,
    
    // Category operations
    handleAddCategory,
    handleRemoveCategory,
    
    // Mutations loading states
    isAddingCategory,
    isRemovingCategory
  };
}

export default useCategoryManagement;
